<template>
  <div
    class="modal-overlay"
  >
    <div
      class="modal-container"
      :class="[`max-w-${size}`, customClass]"
    >
      <!-- Header -->
      <div class="modal-header">
        <h3 class="text-xl font-bold text-gray-900">
          <slot name="title">{{ title }}</slot>
        </h3>
        <button
          @click="$emit('close')"
          class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          :aria-label="translations.closeLabel"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <slot />
      </div>

      <!-- Footer (optional) -->
      <div v-if="$slots.footer" class="modal-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'xl',
    validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl'].includes(value)
  },
  customClass: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const translations = computed(() => {
  try {
    return {
      closeLabel: t('common.modal.close')
    };
  } catch (error) {
    return {
      closeLabel: 'Close'
    };
  }
});

const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    emit('close');
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  document.body.style.overflow = 'hidden';
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});
</script>

<style scoped>
/* Modal size classes - Updated with wider defaults */
.max-w-sm { max-width: 28rem; }  /* 448px - was 24rem */
.max-w-md { max-width: 36rem; }  /* 576px - was 28rem */
.max-w-lg { max-width: 44rem; }  /* 704px - was 32rem */
.max-w-xl { max-width: 56rem; }  /* 896px - was 36rem - NEW DEFAULT */
.max-w-2xl { max-width: 64rem; } /* 1024px - was 42rem */
.max-w-3xl { max-width: 72rem; } /* 1152px - was 48rem */
.max-w-4xl { max-width: 80rem; } /* 1280px - was 56rem */
.max-w-5xl { max-width: 88rem; } /* 1408px - was 64rem */
.max-w-6xl { max-width: 96rem; } /* 1536px - was 72rem */
.max-w-7xl { max-width: 104rem; } /* 1664px - was 80rem */

/* Ensure modal appears above everything */
.modal-overlay {
  z-index: 1000 !important;
}

.modal-container {
  z-index: 1001 !important;
}
</style>
